import { jwtDecode, JwtPayload } from "jwt-decode";
import * as v from "valibot";
import { pipe } from "valibot";
import { AuthUser, BrokeredProvider } from "../types/auth";

/**
 * Represents an access token or provider token along with its expiration time.
 */
export type Token = {
    /** The raw token string */
    value: string;
    /** Expiration time as a timestamp (milliseconds since epoch) */
    expiresAtMs: number;
};

/**
 * Result of a Keycloak broker token exchange: the brokered identity provider's access token and the
 * stable external user id extracted from the brokered id_token (Microsoft `oid`).
 */
export type TokenExchangeResult = {
    /** The brokered identity provider access token and its expiry. */
    providerToken: Token;
    /** The user's stable external id at the brokered identity provider (Microsoft `oid`). */
    externalId: string;
};

/**
 * The authenticated branch of the session state: the user info and the tokens obtained after a
 * successful login or token refresh. Each platform service assembles it from its extracted
 * {@link KeycloakTokens} plus the broker {@link TokenExchangeResult}, then stores it via
 * `AuthSession.setAuthenticated`.
 */
export type AuthenticatedState = {
    status: "authenticated";
    authUser: AuthUser;
    brokeredProvider: BrokeredProvider;
    keycloakAccessToken: Token;
    keycloakIdToken: string;
    keycloakRefreshToken: string;
    providerAccessToken: Token;
};

/**
 * The Keycloak tokens each platform service extracts from its own token response. Its keys are a
 * subset of {@link AuthenticatedState}, so a service can spread it straight into the session state
 * alongside the brokered provider token.
 */
export type KeycloakTokens = {
    keycloakAccessToken: Token;
    keycloakIdToken: string;
    keycloakRefreshToken: string;
};

/**
 * Safety margin applied when checking token expiration to avoid clock skew and in-flight expiry.
 * keycloak minValidity is hardcoded to 5 seconds.
 */
const EXPIRY_BUFFER_MS = 5000;

/**
 * Platform-agnostic helpers shared by the web ({@link ./KeycloakOAuthService}) and native
 * ({@link ./KeycloakOAuthService.native}) Keycloak OAuth services. Every method is static and
 * stateless: it takes its inputs as parameters and holds no instance state, so both services can
 * reuse the exact same Keycloak broker / Mailvista backend logic without duplicating it.
 */
export class KeycloakOAuthShared {
    /**
     * Runs `operation` under `key`, de-duplicating concurrent calls: while one run is in flight,
     * any further call with the same key returns the same shared promise.
     * @param inFlight The map tracking in-flight operations, owned by the calling service.
     * @param key The key to de-duplicate calls by.
     * @param operation The async operation to run, typically a redirect or token refresh.
     * @returns A promise that resolves or rejects with the result of `operation`.
     */
    public static dedup<T>(
        inFlight: Map<string, Promise<unknown>>,
        key: string,
        operation: () => Promise<T>,
    ): Promise<T> {
        const existing = inFlight.get(key) as Promise<T> | undefined;
        if (existing) {
            return existing;
        }

        const promise = operation().finally(() => {
            inFlight.delete(key);
        });
        inFlight.set(key, promise);
        return promise;
    }

    /**
     * Checks if either the provider token or the idp token is expired or about to expire within the expiry buffer.
     * @param accessToken The current Keycloak access token.
     * @param providerToken The brokered identity provider token.
     */
    public static areTokensExpired(
        accessToken: Token,
        providerToken: Token,
    ): boolean {
        const now = Date.now();
        const threshold = now + EXPIRY_BUFFER_MS;
        return (
            accessToken.expiresAtMs <= threshold ||
            providerToken.expiresAtMs <= threshold
        );
    }

    /**
     * Retrieves the brokered identity provider's access token stored by Keycloak, via the broker token
     * retrieval endpoint (/broker/{provider}/token), and extracts the user's stable external id from
     * the brokered id_token.
     * @param accessToken The current Keycloak access token, used to authenticate the broker call.
     * @param authority The Keycloak realm authority URL.
     * @param brokeredProvider The brokered identity provider whose token is being retrieved.
     * @param previousProviderToken The provider token currently stored, if any, used to detect whether
     * Keycloak actually reissued the token (see {@link _resolveProviderTokenExpiry}).
     */
    public static async performTokenExchange(
        accessToken: string,
        authority: string,
        brokeredProvider: BrokeredProvider,
        previousProviderToken?: Token,
    ): Promise<TokenExchangeResult> {
        const brokerUrl = `${authority}/broker/${brokeredProvider}/token`;

        // Freeze the clock before the request so `expires_in` is measured from a point we know precedes
        // the token's issuance, never overestimating its remaining lifetime.
        const requestedAtMs = Date.now();

        const response = await fetch(brokerUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Keycloak broker error: ${response.status}`);
        }

        const raw = await response.json();

        const data = v.parse(
            v.object({
                access_token: v.pipe(
                    v.string(),
                    v.description(
                        "The idp token returned by Keycloak after token exchange",
                    ),
                ),
                id_token: v.pipe(
                    v.string(),
                    v.description(
                        "The idp id_token returned by Keycloak after token exchange",
                    ),
                ),
                expires_in: v.pipe(
                    v.optional(v.number(), 3600),
                    v.description(
                        "Expiration time in seconds for the idp token. Default to 3600 seconds (1 hour)",
                    ),
                ),
            }),
            raw,
        );

        return {
            providerToken: this._resolveProviderTokenExpiry(
                data.access_token,
                data.expires_in,
                requestedAtMs,
                previousProviderToken,
            ),
            externalId: this._extractExternalId(
                data.id_token,
                brokeredProvider,
            ),
        };
    }

    /**
     * Computes the expiry of the brokered provider token from `expires_in` (no JWT decoding).
     *
     * Keycloak's broker endpoint only reissues the idp token once the stored one has actually expired;
     * otherwise it returns the same token, whose `expires_in` no longer reflects a fresh lifetime. So
     * when the returned token matches the one we already hold, we keep the stored expiry untouched.
     * Otherwise the token is fresh and we base its expiry on `expires_in`, measured from `requestedAtMs`.
     * @param accessToken The brokered provider access token returned by Keycloak.
     * @param expiresInSeconds The token's remaining lifetime in seconds, as reported by Keycloak.
     * @param requestedAtMs The timestamp frozen just before the broker request.
     * @param previousProviderToken The provider token currently stored, if any.
     * @private
     */
    private static _resolveProviderTokenExpiry(
        accessToken: string,
        expiresInSeconds: number,
        requestedAtMs: number,
        previousProviderToken?: Token,
    ): Token {
        if (previousProviderToken?.value === accessToken) {
            return previousProviderToken;
        }
        return {
            value: accessToken,
            expiresAtMs: requestedAtMs + expiresInSeconds * 1000,
        };
    }

    /**
     * Extracts the user's stable external id from the brokered id_token claims. For Microsoft this is
     * the `oid` claim (the immutable Azure AD object id). Decodes claims only — no signature or expiry
     * check — and throws if the expected claim is missing.
     * @param idToken The brokered identity provider id_token.
     * @param brokeredProvider The brokered identity provider the id_token comes from.
     * @private
     */
    private static _extractExternalId(
        idToken: string,
        brokeredProvider: BrokeredProvider,
    ): string {
        if (brokeredProvider !== BrokeredProvider.Microsoft) {
            throw new Error(
                `Unsupported brokered provider for external id extraction: ${brokeredProvider}`,
            );
        }

        const { oid } = v.parse(
            v.object({
                oid: v.pipe(
                    v.string(),
                    v.description(
                        "The Microsoft Azure AD object id (immutable external user id).",
                    ),
                ),
            }),
            jwtDecode<JwtPayload>(idToken),
        );

        return oid;
    }

    /**
     * Builds the application's {@link AuthUser} from the decoded profile claims, validating that the
     * required fields are present and that an email claim is available.
     *
     * AB#1831 — the `email` claim (the mail provider address) is authoritative. `preferred_username`
     * is the Keycloak username: it is purely informational and may be an unrelated alias (seen in
     * production: `xavier@mailvista.ai` vs the real `xavier.malaurie@teamway.fr`) or not even an
     * email. It is only used as a last-resort fallback when the `email` claim is missing, and only
     * if it is email-shaped. The resulting email is lowercased: it is used as a lookup key by the
     * managers (Stripe customer, custom views, consent) and by the backend partition keys.
     * @param profile The decoded ID token claims.
     * @param externalUserId The Mailvista backend's external user id for the authenticated user.
     */
    public static buildAuthUser(
        profile: Record<string, unknown>,
        externalUserId: string,
    ): AuthUser {
        const claims = v.parse(
            v.object({
                sub: v.string(),
                // Plain string on purpose: a non-email Keycloak username must not crash the login
                preferred_username: v.optional(v.string()),
                email: v.optional(pipe(v.string(), v.email())),
                name: v.optional(v.string()),
            }),
            profile,
        );

        // Fallback only when preferred_username happens to be email-shaped
        const usernameAsEmailFallback =
            claims.preferred_username &&
            v.is(pipe(v.string(), v.email()), claims.preferred_username)
                ? claims.preferred_username
                : undefined;

        const email = (claims.email ?? usernameAsEmailFallback)?.toLowerCase();

        if (!email) {
            throw new Error(
                "Authenticated user is missing email claim, cannot construct AuthUser object",
            );
        }

        return {
            id: claims.sub,
            name: claims.name || "",
            email: email,
            // Informational only — never use as a lookup key or filter (AB#1831)
            username: claims.preferred_username ?? email,
            externalUserId: externalUserId,
        };
    }
}
